import { identityApi } from "@/modules/identity/api/identityApi";
import { panelsApi } from "@/modules/panels/api/panelsApi";
import { hasPanelPermission } from "@/utils/panelAccess.mjs";
import { createStore } from "vuex";
import Cookies from "js-cookie"; // Dodaj obsługę ciasteczek
import tasks from "@/modules/tasks/store/tasksModule";
import {
  clearPermissionSessionCache,
  permissionsAfterFailedRefresh,
  readPermissionSessionCache,
  writePermissionSessionCache,
} from "@/modules/identity/domain/permissionSessionCache.mjs";

const cachedPermissions = readPermissionSessionCache(window.sessionStorage);
const authCookieOptions = {
  expires: 9 / 24,
  path: "/",
  secure: window.location.protocol === "https:",
  sameSite: "Strict",
};
export default createStore({
  modules: { tasks },
  state: {
    user: null,
    token: Cookies.get("auth_token") || null, // Pobierz token przy starcie aplikacji
    userType: localStorage.getItem("user_type") || null, // Pobranie userType z localStorage
    isLoading: false,
    permissions: cachedPermissions ?? [], // lista kluczy, które user ma (np. ["dostawy_dan", "parametry", ...])
    permissionsLoaded: cachedPermissions !== null, // czy już pobrano uprawnienia w tej sesji
  },
  mutations: {
    SET_LOADING(state, payload) {
      state.isLoading = payload;
    },
    setUser(state, payload) {
      state.user = payload;
      state.userType = payload?.user_type || null; // Pobierz typ użytkownika
      localStorage.setItem("user_type", state.userType);
    },
    setToken(state, token) {
      clearPermissionSessionCache(window.sessionStorage);
      state.token = token;
      Cookies.set("auth_token", token, authCookieOptions);
    },
    SET_PERMISSIONS(state, perms) {
      state.permissions = perms || [];
      state.permissionsLoaded = true;
      writePermissionSessionCache(window.sessionStorage, state.permissions);
    },

    clearUser(state) {
      state.user = null;
      state.token = null;
      state.userType = null; // Resetuj również userType
      localStorage.removeItem("user_type");
      state.permissions = [];
      state.permissionsLoaded = false;
      clearPermissionSessionCache(window.sessionStorage);
    },
  },
  actions: {
    async fetchUser({ commit }) {
      try {
        const response = await identityApi.currentUser();
        commit("setUser", response.data);
        return true;
      } catch (error) {
        commit("clearUser");
        return false;
      }
    },
    async fetchPermissions({ commit, state }) {
      try {
        // Jeśli masz dedykowany endpoint, podmień na np. '/me/permissions'
        const { data } = await panelsApi.currentPanel();
        const list = Array.isArray(data) ? data : [];

        // bierzemy klucze z odpowiedzi i usuwamy duplikaty/puste
        const perms = [
          ...new Set(list.map((row) => row?.klucz).filter(Boolean)),
        ];

        // (opcjonalnie) jeśli backend nie zwraca admin-only, możesz dopisać ręcznie:
        // jeśli backend zwraca kafel "tworzenie_paneli" tylko adminowi, to nic nie rób;
        // jeśli zwraca wszystkim, a chcesz ukryć dla nie-adminów, dodaj warunek tutaj.

        commit("SET_PERMISSIONS", perms);
        return perms;
      } catch (_) {
        const fallback = permissionsAfterFailedRefresh(
          state.permissions,
          state.permissionsLoaded,
        );
        if (!state.permissionsLoaded) commit("SET_PERMISSIONS", fallback);
        return fallback;
      }
    },

    setLoading({ commit }, isLoading) {
      commit("SET_LOADING", isLoading);
    },
    setUser({ commit }, payload) {
      commit("setUser", payload);
    },
    setToken({ commit }, token) {
      commit("setToken", token);
    },
    clearUser({ commit }) {
      commit("clearUser");
      Cookies.remove("auth_token", { path: "/" }); // Usuń token z ciasteczek
    },
  },
  getters: {
    isAuthenticated: (state) => !!state.token,
    userType: (state) => state.userType, // Pobieraj bezpośrednio userType
    token: (state) => state.token, // Getter zwracający token
    userName: (state) => (state.user ? state.user.imie : ""),
    userLastName: (state) => (state.user ? state.user.nazwisko : ""),
    userEmail: (state) => (state.user ? state.user.email : ""),
    //  getter zwracający ID użytkownika
    userId: (state) => (state.user ? state.user.id : null),
    permissions: (state) => state.permissions,
    permissionsLoaded: (state) => state.permissionsLoaded,
    hasPermission: (state) => (key) =>
      hasPanelPermission(
        { userType: state.userType, permissions: state.permissions },
        key
      ),
  },
});
